import { Component, ElementRef, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment-timezone';
import {
  buildVoyageDayIndex,
  buildVoyageHistoryGroups,
  findNearestVoyageSnapshot,
  formatVoyageDate,
  formatVoyageLongDate,
  formatVoyageTime,
  normalizeVoyageHistory,
  voyageCoverage,
  voyageDayKey,
  voyageDensityLevel,
  VoyageCoverage,
  VoyageDaySummary,
  VoyageHistoryRow,
  VoyageSnapshotEntry,
} from '../refresh-history/voyage-refresh-history.model';

export interface VoyageHistoryBrowserData {
  /** The sensor's raw `refreshHistory`. Read only — never mutated here. */
  history: any[];
  /** refreshId currently drawn on the map, so the archive can mark it ON MAP. */
  activeRefreshId: string | null;
  /** IANA zone for the Local display toggle. Omitted disables the toggle. */
  timezone?: string;
  /**
   * Download a record without closing. The modal is the only place an older
   * snapshot can be downloaded now that the panel caps at its recent rows, but
   * downloading is a host job (it needs the collection/sensor indices), so the
   * host hands the work down rather than the modal reaching for a service.
   */
  onDownload?: (record: any) => void;
}

export interface VoyageHistoryBrowserResult {
  /** The chosen refresh-history record, ready for the host's show path. */
  record: any;
}

/** One date in the density calendar. `day === 0` is leading/trailing padding. */
interface VoyageCalCell {
  day: number;
  dayMs: number | null;
  key: string | null;
  count: number;
  level: 0 | 1 | 2 | 3 | 4;
  today: boolean;
  selected: boolean;
  tooltip: string;
}

/** A quick-jump chip and the snapshot it resolves to. */
interface VoyageJumpChip {
  label: string;
  entry: VoyageSnapshotEntry | null;
  disabled: boolean;
  tooltip: string;
}

/**
 * Voyage Radar "Browse snapshots" — the archive half of the snapshot UI.
 *
 * The left panel's `app-voyage-refresh-history` owns the recent tail and the
 * "what is on the map right now" state; this owns everything older. The split
 * is deliberate: the panel answers a glance, this answers a search, and neither
 * tries to be the other. They render the same row grammar from the same model
 * so crossing between them reads as one list at two zoom levels.
 *
 * Nothing here talks to the map, the API or S3. It resolves a record and hands
 * it back; the host decides what showing it means. In particular this dialog
 * NEVER clears the map — clearing stays on the panel, so "Show on map" can
 * never be mistaken for a toggle.
 */
@Component({
  selector: 'app-voyage-history-browser',
  templateUrl: './voyage-history-browser.component.html',
  styleUrls: ['./voyage-history-browser.component.scss'],
})
export class VoyageHistoryBrowserComponent {
  readonly weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  /** Newest-first, unparseable records already dropped. */
  readonly entries: VoyageSnapshotEntry[];
  readonly dayIndex: Map<string, VoyageDaySummary>;
  readonly coverage: VoyageCoverage | null;
  readonly chips: VoyageJumpChip[] = [];
  readonly activeRefreshId: string | null;
  readonly timezone: string | null;

  /**
   * Captured once. A modal is open for seconds, and a `now` that drifted
   * mid-session would make the chips resolve to something other than what their
   * tooltips promised.
   */
  private readonly now = Date.now();

  /** First day of the displayed month, UTC midnight. */
  visibleMonth: Date;
  weeks: VoyageCalCell[][] = [];
  selectedDayKey = '';
  selectedRefreshId: string | null = null;

  tz: 'utc' | 'local' = 'utc';

  private _rowsKey = '';
  private _rows: VoyageHistoryRow[] = [];

  constructor(
    private dialogRef: MatDialogRef<VoyageHistoryBrowserComponent, VoyageHistoryBrowserResult>,
    private host: ElementRef<HTMLElement>,
    @Inject(MAT_DIALOG_DATA) public data: VoyageHistoryBrowserData
  ) {
    this.entries = normalizeVoyageHistory(data?.history);
    this.dayIndex = buildVoyageDayIndex(data?.history);
    this.coverage = voyageCoverage(data?.history);
    this.activeRefreshId = data?.activeRefreshId ?? null;
    this.timezone = data?.timezone || null;

    // Open where the user already is: the snapshot on the map, else the newest.
    const opening =
      this.entries.find((e) => e.refreshId === this.activeRefreshId) ?? this.entries[0] ?? null;

    this.visibleMonth = this._monthStart(opening ? opening.timeMs : this.now);
    if (opening) {
      this.selectedRefreshId = opening.refreshId;
      this.selectedDayKey = voyageDayKey(opening.timeMs);
    }

    this.chips = this._buildChips();
    this._rebuildCalendar();
  }

  // ── Header ─────────────────────────────────────────────────────────────────

  /** "214 snapshots · 12 – 28 Jul 2026". The whole extent, stated up front. */
  get coverageLabel(): string {
    if (!this.coverage) return 'No snapshots';
    const { total, minMs, maxMs } = this.coverage;
    const noun = total === 1 ? 'snapshot' : 'snapshots';
    return `${total} ${noun} · ${formatVoyageDate(minMs)} – ${formatVoyageDate(maxMs)}`;
  }

  // ── Calendar ───────────────────────────────────────────────────────────────

  get monthLabel(): string {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[this.visibleMonth.getUTCMonth()]} ${this.visibleMonth.getUTCFullYear()}`;
  }

  /** Month arrows stop at the archive's edges — there is nothing beyond them. */
  get canGoPrev(): boolean {
    if (!this.coverage) return false;
    return this.visibleMonth.getTime() > this._monthStart(this.coverage.minMs).getTime();
  }

  get canGoNext(): boolean {
    if (!this.coverage) return false;
    return this.visibleMonth.getTime() < this._monthStart(this.coverage.maxMs).getTime();
  }

  prevMonth(): void {
    if (this.canGoPrev) this._shiftMonth(-1);
  }

  nextMonth(): void {
    if (this.canGoNext) this._shiftMonth(1);
  }

  /**
   * Picking a day selects that day's newest snapshot. Every day click therefore
   * lands on something real, which is what lets "Show on map" stay meaningful
   * at all times.
   */
  selectDay(cell: VoyageCalCell): void {
    if (!cell.key || !cell.count) return;
    this.selectedDayKey = cell.key;

    const newest = this._entriesForDay(cell.key)[0];
    if (newest) this._select(newest.refreshId);
    this._rebuildCalendar();
  }

  // ── Day list ───────────────────────────────────────────────────────────────

  get dayLabel(): string {
    const summary = this.dayIndex.get(this.selectedDayKey);
    return summary ? formatVoyageLongDate(summary.dayMs) : '';
  }

  get dayCountLabel(): string {
    const summary = this.dayIndex.get(this.selectedDayKey);
    if (!summary) return '';
    return `${summary.count} ${summary.count === 1 ? 'snapshot' : 'snapshots'}`;
  }

  /**
   * Rows for the selected day, memoised — Angular re-evaluates template getters
   * on every change-detection pass and a fresh array each time would re-render
   * the list. `latestId` is pinned to the archive's newest so the LATEST badge
   * means what it does in the panel, not "newest of this day".
   */
  get rows(): VoyageHistoryRow[] {
    const key = `${this.selectedDayKey}|${this.tz}`;
    if (key === this._rowsKey) return this._rows;

    this._rowsKey = key;
    const records = this._entriesForDay(this.selectedDayKey).map((e) => e.data);
    const groups = buildVoyageHistoryGroups(records, {
      now: this.now,
      latestId: this.coverage?.latestId ?? null,
    });
    this._rows = groups[0]?.rows ?? [];
    return this._rows;
  }

  isSelected(refreshId: string): boolean {
    return refreshId === this.selectedRefreshId;
  }

  isOnMap(refreshId: string): boolean {
    return !!this.activeRefreshId && refreshId === this.activeRefreshId;
  }

  pickRow(row: VoyageHistoryRow): void {
    this._select(row.refreshId);
  }

  downloadRow(row: VoyageHistoryRow, event: Event): void {
    event.stopPropagation();
    this.data?.onDownload?.(row.data);
  }

  // ── Time display ───────────────────────────────────────────────────────────

  /** "14:16" in whichever zone the toggle is on. */
  displayTime(timeMs: number): string {
    const zone = this.timezone;
    if (this.tz === 'utc' || !zone) return formatVoyageTime(timeMs);
    return moment.utc(timeMs).tz(zone).format('HH:mm');
  }

  /**
   * "+1d" / "−1d" when a local time falls on a different date than the UTC day
   * heading it. The archive's spine stays UTC — the toggle relabels times only —
   * so without this a row could read 23:40 under a header a day later and look
   * like a bug.
   */
  dayShift(timeMs: number): string {
    const zone = this.timezone;
    if (this.tz === 'utc' || !zone) return '';
    const utcDay = moment.utc(timeMs).format('YYYY-MM-DD');
    const localDay = moment.utc(timeMs).tz(zone).format('YYYY-MM-DD');
    if (utcDay === localDay) return '';
    return localDay > utcDay ? '+1d' : '−1d';
  }

  setTz(tz: 'utc' | 'local'): void {
    this.tz = tz;
  }

  // ── Quick jumps ────────────────────────────────────────────────────────────

  applyChip(chip: VoyageJumpChip): void {
    if (chip.disabled || !chip.entry) return;
    this._jumpTo(chip.entry);
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  get selectedEntry(): VoyageSnapshotEntry | null {
    if (!this.selectedRefreshId) return null;
    return this.entries.find((e) => e.refreshId === this.selectedRefreshId) ?? null;
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Disabled on the snapshot already drawn: this dialog shows, it never clears. */
  get canShow(): boolean {
    const entry = this.selectedEntry;
    return !!entry && !this.isOnMap(entry.refreshId);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  show(): void {
    const entry = this.selectedEntry;
    if (!entry || !this.canShow) return;
    this.dialogRef.close({ record: entry.data });
  }

  // ── Keyboard ───────────────────────────────────────────────────────────────

  /**
   * ←/→ day, ↑/↓ snapshot, PgUp/PgDn month, Home latest, Enter confirm.
   * The whole archive is reachable without a mouse — which the four-field form
   * this replaces was not.
   *
   * No longer advertised in the footer: the shortcuts are a shortcut, and a
   * legend for them sat under a dialog whose every act is one click away.
   */
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Anything typed into a field keeps its own arrows, Enter and Home.
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

    switch (event.key) {
      case 'ArrowLeft':
        this._shiftDay(-1);
        break;
      case 'ArrowRight':
        this._shiftDay(1);
        break;
      case 'ArrowUp':
        this._shiftRow(-1);
        break;
      case 'ArrowDown':
        this._shiftRow(1);
        break;
      case 'PageUp':
        this.prevMonth();
        break;
      case 'PageDown':
        this.nextMonth();
        break;
      case 'Home':
        if (this.entries.length) this._jumpTo(this.entries[0]);
        break;
      case 'Enter':
        this.show();
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private _entriesForDay(key: string): VoyageSnapshotEntry[] {
    if (!key) return [];
    return this.entries.filter((e) => voyageDayKey(e.timeMs) === key);
  }

  private _select(refreshId: string): void {
    this.selectedRefreshId = refreshId;
    this._scrollRowIntoView(refreshId);
  }

  /** Moves the calendar, the day list and the selection onto one snapshot. */
  private _jumpTo(entry: VoyageSnapshotEntry): void {
    this.selectedDayKey = voyageDayKey(entry.timeMs);
    this.visibleMonth = this._monthStart(entry.timeMs);
    this._select(entry.refreshId);
    this._rebuildCalendar();
  }

  /** Next/previous day that actually has snapshots — empty days are skipped. */
  private _shiftDay(direction: -1 | 1): void {
    const days = Array.from(this.dayIndex.values()).sort((a, b) => a.dayMs - b.dayMs);
    const index = days.findIndex((d) => d.key === this.selectedDayKey);
    const next = days[index + direction];
    if (!next) return;

    this.selectedDayKey = next.key;
    this.visibleMonth = this._monthStart(next.dayMs);
    const newest = this._entriesForDay(next.key)[0];
    if (newest) this._select(newest.refreshId);
    this._rebuildCalendar();
  }

  private _shiftRow(direction: -1 | 1): void {
    const rows = this.rows;
    const index = rows.findIndex((r) => r.refreshId === this.selectedRefreshId);
    const next = rows[index + direction];
    if (!next) return;
    this._select(next.refreshId);
  }

  private _scrollRowIntoView(refreshId: string): void {
    // After the list has re-rendered for the new day.
    setTimeout(() => {
      const el = this.host.nativeElement.querySelector(
        `[data-snap="${refreshId}"]`
      ) as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  private _shiftMonth(delta: number): void {
    this.visibleMonth = new Date(Date.UTC(
      this.visibleMonth.getUTCFullYear(),
      this.visibleMonth.getUTCMonth() + delta,
      1
    ));
    this._rebuildCalendar();
  }

  private _monthStart(timeMs: number): Date {
    const d = new Date(timeMs);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  }

  private _rebuildCalendar(): void {
    const y = this.visibleMonth.getUTCFullYear();
    const m = this.visibleMonth.getUTCMonth();
    const startDow = new Date(Date.UTC(y, m, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();

    const todayKey = voyageDayKey(this.now);
    const blank = (): VoyageCalCell => ({
      day: 0, dayMs: null, key: null, count: 0, level: 0,
      today: false, selected: false, tooltip: '',
    });

    const cells: VoyageCalCell[] = [];
    for (let i = 0; i < startDow; i++) cells.push(blank());

    for (let day = 1; day <= daysInMonth; day++) {
      const dayMs = Date.UTC(y, m, day);
      const key = voyageDayKey(dayMs);
      const summary = this.dayIndex.get(key);
      const count = summary?.count ?? 0;
      cells.push({
        day,
        dayMs,
        key,
        count,
        level: voyageDensityLevel(count),
        today: key === todayKey,
        selected: key === this.selectedDayKey,
        tooltip: this._cellTooltip(dayMs, summary),
      });
    }

    while (cells.length % 7 !== 0) cells.push(blank());

    this.weeks = [];
    for (let i = 0; i < cells.length; i += 7) this.weeks.push(cells.slice(i, i + 7));
  }

  /** Lets the user read a day's contents before committing to opening it. */
  private _cellTooltip(dayMs: number, summary?: VoyageDaySummary): string {
    const date = formatVoyageDate(dayMs);
    if (!summary) return `${date} — no refreshes`;

    const lines = [
      date,
      `${summary.count} ${summary.count === 1 ? 'snapshot' : 'snapshots'}`,
      `${formatVoyageTime(summary.firstMs)} – ${formatVoyageTime(summary.lastMs)} UTC`,
    ];
    if (summary.minVessels != null && summary.maxVessels != null) {
      lines.push(
        summary.minVessels === summary.maxVessels
          ? `${summary.maxVessels.toLocaleString()} vessels`
          : `${summary.minVessels.toLocaleString()} – ${summary.maxVessels.toLocaleString()} vessels`
      );
    }
    return lines.join('\n');
  }

  /**
   * Chips resolve against real snapshots at build time, and each one's tooltip
   * states exactly where it will land — including how far that is from the
   * instant it was measured from. Nothing is disabled for being "far": a chip
   * that silently refused would be less useful than one that lands and named
   * its landing spot before it was pressed.
   */
  private _buildChips(): VoyageJumpChip[] {
    if (!this.entries.length) return [];

    const chips: VoyageJumpChip[] = [];
    const newest = this.entries[0];
    const oldest = this.entries[this.entries.length - 1];

    chips.push({
      label: 'Latest',
      entry: newest,
      disabled: false,
      tooltip: `${formatVoyageDate(newest.timeMs)} · ${formatVoyageTime(newest.timeMs)} UTC`,
    });

    for (const hours of [6, 12, 24]) {
      const targetMs = this.now - hours * 3600000;
      const nearest = findNearestVoyageSnapshot(this.data?.history, targetMs);
      chips.push({
        label: `−${hours}h`,
        entry: nearest?.entry ?? null,
        disabled: !nearest,
        // Both sides carry a date: an offset chip can land on a different day
        // than the one it was measured from, and a bare "14:00" would not say so.
        tooltip: nearest
          ? `Nearest to ${formatVoyageDate(targetMs)} ${formatVoyageTime(targetMs)} UTC → ` +
            `${formatVoyageDate(nearest.entry.timeMs)} ${formatVoyageTime(nearest.entry.timeMs)} UTC`
          : 'No snapshots',
      });
    }

    // Last snapshot of yesterday (UTC) — distinct from −24h, which is relative
    // to the clock rather than to the calendar day.
    const yesterdayKey = voyageDayKey(this.now - 86400000);
    const yesterday = this._entriesForDay(yesterdayKey)[0] ?? null;
    chips.push({
      label: 'Yesterday',
      entry: yesterday,
      disabled: !yesterday,
      tooltip: yesterday
        ? `${formatVoyageDate(yesterday.timeMs)} · ${formatVoyageTime(yesterday.timeMs)} UTC`
        : 'No refreshes yesterday',
    });

    chips.push({
      label: 'Oldest',
      entry: oldest,
      disabled: false,
      tooltip: `${formatVoyageDate(oldest.timeMs)} · ${formatVoyageTime(oldest.timeMs)} UTC`,
    });

    return chips;
  }
}
